/**
 * "Can I send this client's status to the company that referred them?", answered on the case page.
 *
 * Lloyd, 15 Sep 2026. Before this the answer lived only inside `mayShareWith`, so a staff member
 * looking at a CDL Consultants email had no way to tell whether replying was allowed.
 *
 * ## Why three colours and not a tick or a cross
 *
 * **Not asked** is grey, not red. A client nobody has asked has not refused, and showing that as a
 * refusal stops work the client would have permitted. Red is reserved for an actual "no", which is
 * the client's decision and is the one thing here that must never be worked around.
 *
 * The chip carries the whole reason in its tooltip rather than making staff open another screen,
 * including who the client named, because "yes" to a payor is not "yes" to an employer.
 */
import React, { useEffect, useState } from 'react';
import { Chip, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CaseConsentApi, { CaseConsentDto } from '../../api/CaseConsentApi';

interface Props {
  Ticket_guid: string;
}

const CaseConsentChip: React.FC<Props> = ({ Ticket_guid }) => {
  const [consent, setConsent] = useState<CaseConsentDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    CaseConsentApi.forCase(Ticket_guid)
      .then((result) => {
        if (!cancelled) setConsent(result);
      })
      // An older API has no such route. The case page must still render, so the chip simply does not
      // appear rather than showing a wrong answer about disclosure.
      .catch(() => {
        if (!cancelled) setConsent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [Ticket_guid]);

  if (!consent?.answer) return null;

  const { icon, color } =
    consent.answer === 'granted'
      ? { icon: <CheckCircleIcon />, color: 'success' as const }
      : consent.answer === 'declined'
        ? { icon: <BlockIcon />, color: 'error' as const }
        : { icon: <HelpOutlineIcon />, color: 'default' as const };

  // The family member's name is already in the reason ("their family member Jane Doe"), so it is
  // not repeated here.
  return (
    <Tooltip title={consent.reason ?? ''}>
      <Chip
        size="small"
        icon={icon}
        color={color}
        variant={consent.answer === 'granted' ? 'filled' : 'outlined'}
        label={`Consent: ${consent.label ?? ''}`}
      />
    </Tooltip>
  );
};

export default CaseConsentChip;
